import { useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
} from "framer-motion";
import { MessageCircle, MapPin, Clock, Phone } from "lucide-react";
import {
  ADDRESS,
  PHONE_PRIMARY_DISPLAY,
  openWhatsApp,
  GENERAL_WHATSAPP_MESSAGE,
} from "@/lib/constants";
import { EASE } from "@/components/Reveal";

const lineVariants = {
  hidden: { y: "115%" },
  show: { y: 0, transition: { duration: 1.15, ease: EASE } },
};

const fadeVariants = {
  hidden: { opacity: 0, y: 24 },
  show: (d) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: d, ease: EASE },
  }),
};

export default function Hero({ onBook }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const smx = useSpring(mx, { stiffness: 55, damping: 18 });
  const smy = useSpring(my, { stiffness: 55, damping: 18 });

  const handleMouse = (e) => {
    mx.set((e.clientX / window.innerWidth - 0.5) * 28);
    my.set((e.clientY / window.innerHeight - 0.5) * 20);
  };

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      onMouseMove={handleMouse}
      className="relative flex min-h-screen flex-col justify-center overflow-hidden"
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(168,197,229,0.4),transparent_55%),radial-gradient(ellipse_at_bottom_left,rgba(176,141,46,0.07),transparent_50%)]" />

      <motion.div
        aria-hidden
        style={{ x: smx, y: smy }}
        className="pointer-events-none absolute -right-[18vw] top-1/2 hidden -translate-y-1/2 select-none md:-right-[10vw] md:block"
      >
        <span className="font-display text-outline-gold text-[34vw] font-semibold italic leading-none">
          R&J
        </span>
      </motion.div>

      <motion.div
        initial="hidden"
        animate="show"
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-40 pt-36 sm:px-8"
      >
        <motion.div
          variants={fadeVariants}
          custom={0.25}
          className="mb-10 flex items-center gap-5"
        >
          <span className="h-px w-16 bg-[#B08D2E]/60" />
          <span className="text-[10px] font-medium uppercase tracking-[0.28em] text-[#B08D2E] sm:text-[11px] sm:tracking-[0.45em]">
            Premium Garment Care · Yiewsley, West Drayton
          </span>
        </motion.div>

        <h1 className="font-display max-w-5xl text-[clamp(2.9rem,8.5vw,6.75rem)] font-light leading-[1.02] tracking-tight text-[#1A1A1A]">
          <span className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
            <motion.span variants={lineVariants} className="block will-change-transform">
              Your Clothes Journey:
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-[0.14em] -mb-[0.06em]">
            <motion.span variants={lineVariants} className="block will-change-transform">
              <em className="gold-text font-medium italic">
                From Pickup to Perfect.
              </em>
            </motion.span>
          </span>
        </h1>

        <motion.p
          variants={fadeVariants}
          custom={0.85}
          className="mt-9 max-w-xl text-base font-light leading-relaxed tracking-wide text-[#6E675C] sm:text-lg"
        >
          Premier Dry Cleaning, Expert Stain Removal, Hand-Finished Pressing
          &amp; Doorstep Laundry Service.
        </motion.p>

        <motion.div
          variants={fadeVariants}
          custom={1.05}
          className="mt-12 flex flex-col gap-4 sm:flex-row sm:items-center"
        >
          <button
            data-testid="hero-book-pickup-btn"
            onClick={onBook}
            className="group relative overflow-hidden bg-[#B08D2E] px-11 py-5 text-xs font-semibold uppercase tracking-[0.28em] text-[#F9F6F0] transition-colors duration-300 hover:bg-[#96771F]"
          >
            <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/50 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-full" />
            Book a Pickup
          </button>
          <button
            data-testid="hero-whatsapp-btn"
            onClick={() => openWhatsApp(GENERAL_WHATSAPP_MESSAGE)}
            className="flex items-center justify-center gap-3 border border-[#B08D2E]/40 px-11 py-5 text-xs font-medium uppercase tracking-[0.28em] text-[#B08D2E] transition-[background-color,border-color,color] duration-300 hover:border-[#B08D2E] hover:bg-[#B08D2E]/10"
          >
            <MessageCircle className="h-4 w-4" strokeWidth={1.5} />
            WhatsApp Us
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute inset-x-0 bottom-0 z-10 border-t border-[#B08D2E]/10 bg-[#F9F6F0]/70 backdrop-blur-md"
      >
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-x-8 gap-y-3 px-5 py-5 sm:grid-cols-3 sm:px-8">
          <div className="flex items-center gap-3 text-xs tracking-wide text-[#6E675C]">
            <MapPin className="h-4 w-4 shrink-0 text-[#B08D2E]" strokeWidth={1.5} />
            <span data-testid="hero-address">{ADDRESS}</span>
          </div>
          <div className="flex items-center gap-3 text-xs tracking-wide text-[#6E675C]">
            <Clock className="h-4 w-4 shrink-0 text-[#B08D2E]" strokeWidth={1.5} />
            <span data-testid="hero-hours">Mon – Sat 9:00 – 19:00 · Sun 10:00 – 16:00</span>
          </div>
          <div className="flex items-center gap-3 text-xs tracking-wide text-[#6E675C]">
            <Phone className="h-4 w-4 shrink-0 text-[#B08D2E]" strokeWidth={1.5} />
            <span data-testid="hero-phone">{PHONE_PRIMARY_DISPLAY}</span>
          </div>
        </div>
      </motion.div>

      <motion.div
        aria-hidden
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-28 right-8 z-10 hidden flex-col items-center gap-4 lg:flex"
      >
        <span className="text-[9px] uppercase tracking-[0.5em] text-[#6E675C] [writing-mode:vertical-rl]">
          Scroll
        </span>
        <span className="h-16 w-px animate-scroll-pulse bg-gradient-to-b from-[#B08D2E] to-transparent" />
      </motion.div>
    </section>
  );
}
